"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormulaField = void 0;
const compute_manager_1 = require("../../compute_manager");
const formula_parser_1 = require("../../formula_parser");
const joi_1 = __importDefault(require("joi"));
const constants_1 = require("../../model/constants");
const utils_1 = require("../../model/utils");
const selectors_1 = require("../../exports/store/selectors");
const types_1 = require("../../types");
const field_types_1 = require("../../types/field_types");
const env_1 = require("../../utils/env");
const checkbox_field_1 = require("./checkbox_field");
const date_time_base_field_1 = require("./date_time_base_field");
const field_1 = require("./field");
const number_base_field_1 = require("./number_base_field");
const stat_1 = require("./stat");
const text_base_field_1 = require("./text_base_field");
const validate_schema_1 = require("./validate_schema");
class FormulaField extends field_1.ArrayValueField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    validateProperty() {
        return FormulaField.propertySchema.validate(this.field.property);
    }
    validateCellValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    validateOpenWriteValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    get apiMetaPropertyFormat() {
        return (0, utils_1.getApiMetaPropertyFormat)(this);
    }
    get apiMetaProperty() {
        const res = {
            expression: this.field.property.expression,
            valueType: this.basicValueType,
            hasError: this.hasError,
        };
        if (this.apiMetaPropertyFormat) {
            res.format = this.apiMetaPropertyFormat;
        }
        return res;
    }
    get openValueJsonSchema() {
        switch (this.basicValueType) {
            case field_types_1.BasicValueType.Number:
            case field_types_1.BasicValueType.String:
            case field_types_1.BasicValueType.DateTime:
            case field_types_1.BasicValueType.Boolean:
                return {
                    type: constants_1.ValueTypeMap[this.basicValueType],
                    title: this.field.name,
                };
            case field_types_1.BasicValueType.Array:
                const innerBasicValueType = this.innerBasicValueType;
                return {
                    type: 'array',
                    title: this.field.name,
                    items: {
                        type: constants_1.ValueTypeMap[innerBasicValueType],
                        title: 'sub project'
                    }
                };
            default:
                // By default, it is processed as string
                return {
                    type: 'string',
                    title: this.field.name,
                };
        }
    }
    getParseResult() {
        const snapshot = (0, selectors_1.getSnapshot)(this.state, this.field.property.datasheetId);
        const fieldMap = snapshot === null || snapshot === void 0 ? void 0 : snapshot.meta.fieldMap;
        return (0, formula_parser_1.parse)(this.field.property.expression, { field: this.field, fieldMap, state: this.state });
    }
    get hasError() {
        const { datasheetId, expression } = this.field.property;
        const cacheResult = formula_parser_1.ExpCache.get(datasheetId, this.field.id, expression);
        if ((0, env_1.isClient)()) {
            const computeRefManager = (0, compute_manager_1.getComputeRefManager)(this.state);
            if (!computeRefManager.checkRef(`${datasheetId}-${this.field.id}`)) {
                return true;
            }
        }
        return Boolean(cacheResult && 'error' in cacheResult);
    }
    get statTypeList() {
        switch (this.valueType) {
            case field_types_1.BasicValueType.Number:
                return number_base_field_1.NumberBaseField._statTypeList;
            case field_types_1.BasicValueType.Boolean:
                return checkbox_field_1.CheckboxField._statTypeList;
            case field_types_1.BasicValueType.DateTime:
                return date_time_base_field_1.DateTimeBaseField._statTypeList;
            case field_types_1.BasicValueType.String:
                return text_base_field_1.TextBaseField._statTypeList;
            default:
                return text_base_field_1.TextBaseField._statTypeList;
        }
    }
    get basicValueType() {
        const formulaExpr = this.getParseResult();
        if ('error' in formulaExpr) {
            return field_types_1.BasicValueType.String;
        }
        return formulaExpr.ast.valueType;
    }
    get innerBasicValueType() {
        const formulaExpr = this.getParseResult();
        if ('error' in formulaExpr) {
            return field_types_1.BasicValueType.String;
        }
        return formulaExpr.ast.innerValueType || field_types_1.BasicValueType.String;
    }
    showFOperatorDesc(type) {
        switch (this.valueType) {
            case field_types_1.BasicValueType.Number:
                return number_base_field_1.NumberBaseField.FOperatorDescMap[type];
            case field_types_1.BasicValueType.Boolean:
                return types_1.FOperatorDescMap[type];
            case field_types_1.BasicValueType.String:
                return types_1.FOperatorDescMap[type];
            case field_types_1.BasicValueType.DateTime:
                return date_time_base_field_1.DateTimeBaseField.FOperatorDescMap[type];
            default:
                return '';
        }
    }
    get acceptFilterOperators() {
        switch (this.valueType) {
            case field_types_1.BasicValueType.Number:
                return number_base_field_1.NumberBaseField._acceptFilterOperators;
            case field_types_1.BasicValueType.Boolean:
                return checkbox_field_1.CheckboxField._acceptFilterOperators;
            case field_types_1.BasicValueType.String:
                return text_base_field_1.TextBaseField._acceptFilterOperators;
            case field_types_1.BasicValueType.DateTime:
                return date_time_base_field_1.DateTimeBaseField._acceptFilterOperators;
            default:
                return [];
        }
    }
    get isComputed() {
        return true;
    }
    // getExpression2Display() {
    //   const
    //   this.field.property.expression;
    // }
    static defaultProperty() {
        return {
            expression: '',
            datasheetId: ''
        };
    }
    compare(cv1, cv2, orderInCellValueSensitive) {
        // Compatible with sorting formula error
        const isCv1Error = cv1 instanceof formula_parser_1.FormulaBaseError;
        const isCv2Error = cv2 instanceof formula_parser_1.FormulaBaseError;
        if (isCv1Error && isCv2Error) {
            return 0;
        }
        if (isCv1Error && !isCv2Error) {
            return -1;
        }
        if (!isCv1Error && isCv2Error) {
            return 1;
        }
        switch (this.valueType) {
            case field_types_1.BasicValueType.Number:
                return number_base_field_1.NumberBaseField._compare(cv1, cv2);
            case field_types_1.BasicValueType.DateTime:
                return date_time_base_field_1.DateTimeBaseField._compare(cv1, cv2, this.field.property.formatting);
            case field_types_1.BasicValueType.Boolean:
                return checkbox_field_1.CheckboxField._compare(cv1, cv2, orderInCellValueSensitive);
            default:
                return super.compare(cv1, cv2, orderInCellValueSensitive);
        }
    }
    recordEditable() {
        return false;
    }
    cellValueToStdValue(cellValue) {
        const stdValue = {
            sourceType: this.field.type,
            data: [],
        };
        if (cellValue != null) {
            stdValue.data.push({
                text: this.cellValueToString(cellValue) || '',
                value: cellValue
            });
        }
        return stdValue;
    }
    cellValueToArray(cellValue) {
        return cellValue;
    }
    arrayValueToString(cellValue, options) {
        if (Array.isArray(cellValue)) {
            const vArray = this.arrayValueToArrayStringValueArray(cellValue, options);
            return vArray == null ? null : vArray.join(', ');
        }
        return String(cellValue);
    }
    arrayValueToArrayStringValueArray(cellValue, _options) {
        return cellValue.map(cv => {
            var _a;
            switch (this.innerBasicValueType) {
                case field_types_1.BasicValueType.Number:
                case field_types_1.BasicValueType.Boolean: {
                    return (0, number_base_field_1.numberFormat)(cv, (_a = this.field.property) === null || _a === void 0 ? void 0 : _a.formatting);
                }
                case field_types_1.BasicValueType.DateTime:
                    return (0, date_time_base_field_1.dateTimeFormat)(cv, this.field.property.formatting, (0, selectors_1.getUserTimeZone)(this.state));
                case field_types_1.BasicValueType.String:
                    return String(cv);
                default:
                    return String(cv);
            }
        });
    }
    stdValueToCellValue(_stdField) {
        return null;
    }
    validate(_value) {
        return true;
    }
    defaultValueForCondition(_condition) {
        return null;
    }
    cellValueToString(cellValue, options) {
        var _a;
        if (cellValue == null) {
            return null;
        }
        switch (this.basicValueType) {
            case field_types_1.BasicValueType.Number:
            case field_types_1.BasicValueType.Boolean: {
                return (0, number_base_field_1.numberFormat)(cellValue, (_a = this.field.property) === null || _a === void 0 ? void 0 : _a.formatting);
            }
            case field_types_1.BasicValueType.DateTime:
                return (0, date_time_base_field_1.dateTimeFormat)(cellValue, this.field.property.formatting, (0, selectors_1.getUserTimeZone)(this.state));
            case field_types_1.BasicValueType.String:
                return String(cellValue);
            case field_types_1.BasicValueType.Array: {
                return this.arrayValueToString(cellValue, options);
            }
            default:
                return null;
        }
    }
    isMeetFilter(operator, cellValue, conditionValue) {
        const innerBasicValueTypeFilter = (cv) => {
            switch (this.innerBasicValueType) {
                case field_types_1.BasicValueType.Number:
                    return number_base_field_1.NumberBaseField._isMeetFilter(operator, cv, conditionValue);
                case field_types_1.BasicValueType.Boolean:
                    return checkbox_field_1.CheckboxField._isMeetFilter(operator, cv, conditionValue);
                case field_types_1.BasicValueType.DateTime:
                    return date_time_base_field_1.DateTimeBaseField._isMeetFilter(operator, cv, conditionValue);
                case field_types_1.BasicValueType.String:
                    return text_base_field_1.TextBaseField._isMeetFilter(operator, this.cellValueToString(cv), conditionValue);
                default:
                    return false;
            }
        };
        switch (this.basicValueType) {
            case field_types_1.BasicValueType.Number:
                return number_base_field_1.NumberBaseField._isMeetFilter(operator, cellValue, conditionValue);
            case field_types_1.BasicValueType.Boolean:
                return checkbox_field_1.CheckboxField._isMeetFilter(operator, cellValue, conditionValue);
            case field_types_1.BasicValueType.DateTime:
                return date_time_base_field_1.DateTimeBaseField._isMeetFilter(operator, cellValue, conditionValue);
            case field_types_1.BasicValueType.String:
                return text_base_field_1.TextBaseField._isMeetFilter(operator, this.cellValueToString(cellValue), conditionValue);
            case field_types_1.BasicValueType.Array:
                switch (operator) {
                    // The filter operation of negative semantics requires that each item in the array satisfies
                    case types_1.FOperator.DoesNotContain:
                    case types_1.FOperator.IsNot:
                    case types_1.FOperator.IsEmpty:
                        return cellValue.every(innerBasicValueTypeFilter);
                    default:
                        return cellValue.some(innerBasicValueTypeFilter);
                }
            default:
                return false;
        }
    }
    statType2text(type) {
        switch (this.valueType) {
            case field_types_1.BasicValueType.DateTime:
                return date_time_base_field_1.DateTimeBaseField._statType2text(type);
            case field_types_1.BasicValueType.Number:
            case field_types_1.BasicValueType.Boolean:
            case field_types_1.BasicValueType.String:
                return stat_1.StatTranslate[type];
            default:
                return '';
        }
    }
    cellValueToApiStandardValue(cellValue) {
        return cellValue;
    }
    cellValueToApiStringValue(cellValue) {
        return this.cellValueToString(cellValue);
    }
    cellValueToOpenValue(cellValue) {
        return cellValue;
    }
    openWriteValueToCellValue() {
        return null;
    }
    get openFieldProperty() {
        const res = {
            expression: this.field.property.expression,
            valueType: this.basicValueType,
            hasError: this.hasError,
        };
        if (this.apiMetaPropertyFormat) {
            res.format = this.apiMetaPropertyFormat;
        }
        return res;
    }
    validateUpdateOpenProperty(updateProperty) {
        return FormulaField.openUpdatePropertySchema.validate(updateProperty);
    }
    updateOpenFieldPropertyTransformProperty(openFieldProperty) {
        const { expression, format } = openFieldProperty;
        const formatting = format ? (0, utils_1.computedFormattingToFormat)(format) : undefined;
        return {
            datasheetId: this.field.property.datasheetId,
            expression: expression || '',
            formatting
        };
    }
    filterValueToOpenFilterValue(value) {
        switch (this.valueType) {
            case field_types_1.BasicValueType.Number:
                return number_base_field_1.NumberBaseField._filterValueToOpenFilterValue(value);
            case field_types_1.BasicValueType.Boolean:
                return checkbox_field_1.CheckboxField._filterValueToOpenFilterValue(value);
            case field_types_1.BasicValueType.String:
                return text_base_field_1.TextBaseField._filterValueToOpenFilterValue(value);
            case field_types_1.BasicValueType.DateTime:
                return date_time_base_field_1.DateTimeBaseField._filterValueToOpenFilterValue(value);
            default:
                return null;
        }
    }
    openFilterValueToFilterValue(value) {
        switch (this.valueType) {
            case field_types_1.BasicValueType.Number:
                return number_base_field_1.NumberBaseField._openFilterValueToFilterValue(value);
            case field_types_1.BasicValueType.Boolean:
                return checkbox_field_1.CheckboxField._openFilterValueToFilterValue(value);
            case field_types_1.BasicValueType.String:
                return text_base_field_1.TextBaseField._openFilterValueToFilterValue(value);
            case field_types_1.BasicValueType.DateTime:
                return date_time_base_field_1.DateTimeBaseField._openFilterValueToFilterValue(value);
            default:
                return null;
        }
    }
    validateOpenFilterValue(value) {
        switch (this.valueType) {
            case field_types_1.BasicValueType.Number:
                return number_base_field_1.NumberBaseField._validateOpenFilterValue(value);
            case field_types_1.BasicValueType.Boolean:
                return checkbox_field_1.CheckboxField._validateOpenFilterValue(value);
            case field_types_1.BasicValueType.String:
                return text_base_field_1.TextBaseField._validateOpenFilterValue(value);
            case field_types_1.BasicValueType.DateTime:
                return date_time_base_field_1.DateTimeBaseField._validateOpenFilterValue(value);
            default:
                return (0, validate_schema_1.joiErrorResult)('formula no support Array filter');
        }
    }
}
exports.FormulaField = FormulaField;
FormulaField.propertySchema = joi_1.default.object({
    datasheetId: (0, validate_schema_1.datasheetIdString)().required(),
    expression: joi_1.default.string().allow('').required(),
    formatting: (0, validate_schema_1.computedFormatting)(),
}).required();
FormulaField.openUpdatePropertySchema = joi_1.default.object({
    expression: joi_1.default.string(),
    format: (0, validate_schema_1.computedFormattingStr)(),
}).required();
//# sourceMappingURL=formula_field.js.map